import { StripeProduct } from '@raydiant/api-client-js';
import { Plan, PlanPerPeriod, DevicePlan, LicensePlan } from '../types';

const LICENSE_PRODUCT_IDENTIFIER = 'service';

const DEFAULT_CURRENCY = 'USD';

const convertCentsToDollars = (cents: number): number => {
  return cents / 100;
};

const isLicenseProduct = (name: string): boolean => {
  return name.toLowerCase().includes(LICENSE_PRODUCT_IDENTIFIER);
};

const isValidPlanPerPeriod = (
  plan: Partial<PlanPerPeriod>,
): plan is PlanPerPeriod => {
  return !!(
    plan.license &&
    plan.device &&
    typeof plan.license.price === 'number' &&
    typeof plan.device.price === 'number'
  );
};

/**
 * Transforms an array of Stripe products into a Plan structure.
 *
 * This function processes Stripe product data and converts it into the internal Plan format
 * used by the application. It identifies license and device products based on product naming
 * conventions, extracts pricing information for monthly and annual billing cycles, and
 * constructs a complete Plan object with features and pricing details.
 *
 * @param products - Array of Stripe products containing pricing information. Must contain at least
 *                   one product. Each product should have a name and prices array with billing cycles.
 *                   Products are identified as license products if their name includes 'service'
 *                   (case-insensitive), otherwise they are treated as device products.
 * @param productIdentifier - String identifier for the product (e.g., 'pro', 'enterprise').
 *                            Used to generate the plan ID and name.
 *
 * @returns A Plan object containing:
 *          - Generated plan ID, name, title, and description
 *          - Predefined feature list
 *          - Monthly and annual pricing for both license and device components
 *
 * @throws {Error} If the products array is empty or null
 *
 * @remarks
 * - Only 'monthly' and 'annually' billing cycles are supported. Other billing cycles are ignored.
 * - If multiple prices exist for the same billing cycle and product type, the first one will be used.
 * - All prices are converted from cents to dollars.
 * - Currency is hardcoded to 'USD' as it is not provided by the API.
 * - Product type detection relies on substring matching for 'service' in the product name.
 */
export const transformStripeProductsToPlan = (
  products: StripeProduct[],
  productIdentifier: string,
): Plan => {
  if (!products || products.length === 0) {
    throw new Error('Products array must contain at least one product');
  }

  const monthlyPlan: Partial<PlanPerPeriod> = {
    license: undefined,
    device: undefined,
  };

  const annuallyPlan: Partial<PlanPerPeriod> = {
    license: undefined,
    device: undefined,
  };

  products.forEach((product) => {
    const isLicense = isLicenseProduct(product.name);

    product.prices.forEach((price) => {
      const priceInDollars = convertCentsToDollars(price.amount);

      if (price.billingCycle === 'monthly') {
        if (isLicense) {
          // Use first price only, skip if already set
          if (!monthlyPlan.license) {
            monthlyPlan.license = {
              currency: DEFAULT_CURRENCY,
              price: priceInDollars,
              billingPeriod: 'monthly',
            } as LicensePlan;
          }
        } else {
          // Use first price only, skip if already set
          if (!monthlyPlan.device) {
            monthlyPlan.device = {
              currency: DEFAULT_CURRENCY,
              price: priceInDollars,
              billingPeriod: null,
            } as DevicePlan;
          }
        }
      } else if (price.billingCycle === 'annually') {
        if (isLicense) {
          // Use first price only, skip if already set
          if (!annuallyPlan.license) {
            annuallyPlan.license = {
              currency: DEFAULT_CURRENCY,
              price: priceInDollars,
              billingPeriod: 'annually',
            } as LicensePlan;
          }
        } else {
          // Use first price only, skip if already set
          if (!annuallyPlan.device) {
            annuallyPlan.device = {
              currency: DEFAULT_CURRENCY,
              price: priceInDollars,
              billingPeriod: null,
            } as DevicePlan;
          }
        }
      } else {
        // Unsupported billing cycle - log warning for debugging
        console.warn('Encountered unsupported billing cycle', {
          productName: product.name,
          billingCycle: price.billingCycle,
          amount: price.amount,
        });
      }
    });
  });

  const plan: Plan = {
    id: `displai-${productIdentifier}`,
    productIdentifier,
    name: `Displai ${
      productIdentifier.charAt(0).toUpperCase() + productIdentifier.slice(1)
    }`,
    planTitle: 'A plan for brilliance!',
    planDescription: 'Everything you need to go live fast',
    features: [
      {
        id: 'player',
        type: 'device',
        name: 'A Displai Player',
      },
      {
        id: 'license',
        type: 'license',
        name: 'A Displai License',
      },
      {
        id: 'upload',
        type: 'feature',
        name: 'Upload, schedule & publish content from one dashboard',
      },
      {
        id: 'locations',
        type: 'feature',
        name: 'Unlimited locations',
      },
      {
        id: 'remote',
        type: 'feature',
        name: 'Manage and monitor screens remotely',
      },
      {
        id: 'playlists',
        type: 'feature',
        name: 'Smart playlists & scheduling tools',
      },
      {
        id: 'apps',
        type: 'feature',
        name: '50+ apps & POS integrations',
      },
      {
        id: 'onboarding',
        type: 'feature',
        name: 'Personalized onboarding',
      },
      {
        id: 'support',
        type: 'feature',
        name: '24/7 support',
      },
    ],
    // Only include monthly/annual if they have valid pricing data
    ...(isValidPlanPerPeriod(monthlyPlan) && { monthly: monthlyPlan }),
    ...(isValidPlanPerPeriod(annuallyPlan) && { annually: annuallyPlan }),
  };

  return plan;
};
